import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import type { Unit } from "@/lib/types/unit";
import type { WeaponData } from "@/lib/types/weapon";

interface WeaponSelectorProps {
  units: Unit[];
  weaponData: Map<string, WeaponData[]>;
  selectedWeapons: Map<string, number[]>;
  onWeaponSelect: (unitDescriptor: string, weaponIndexes: number[]) => void;
}

export function WeaponSelector({
  units,
  weaponData,
  selectedWeapons,
  onWeaponSelect,
}: WeaponSelectorProps) {
  // Filter to only units that have weapons
  const unitsWithWeapons = units.filter((unit) => {
    if (!unit.weaponDescriptorName) return false;
    const weapons = weaponData.get(unit.weaponDescriptorName);
    return weapons && weapons.length > 0;
  });

  if (unitsWithWeapons.length === 0) {
    return null;
  }

  const handleWeaponToggle = (unitDescriptor: string, weaponIndex: number) => {
    const currentSelection = selectedWeapons.get(unitDescriptor) || [];
    const newIndexes = currentSelection.includes(weaponIndex)
      ? currentSelection.filter((i) => i !== weaponIndex)
      : [...currentSelection, weaponIndex];
    
    // Ensure at least one weapon is always selected
    if (newIndexes.length === 0) {
      return;
    }
    
    onWeaponSelect(unitDescriptor, newIndexes);
  };

  return (
    <div className="space-y-4">
      {unitsWithWeapons.map((unit) => {
        const weapons = weaponData.get(unit.weaponDescriptorName || "");
        if (!weapons || weapons.length === 0) return null;

        const selectedIndexes = selectedWeapons.get(unit.descriptorName) || [];

        return (
          <div key={unit.descriptorName} className="space-y-2">
            <label className="text-sm font-medium">{unit.name}</label>
            <div className="flex flex-wrap gap-2">
              {weapons.map((weapon, index) => {
                const isSelected = selectedIndexes.includes(index);

                return (
                  <div
                    key={weapon.salvoIndex}
                    className={`flex items-center gap-1.5 px-2 py-1 rounded border transition-all cursor-pointer ${
                      isSelected
                        ? "border-primary bg-primary/10"
                        : "border-muted hover:border-muted-foreground/40 hover:bg-muted/50"
                    }`}
                    onClick={() => handleWeaponToggle(unit.descriptorName, index)}
                  >
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => handleWeaponToggle(unit.descriptorName, index)}
                      id={`weapon-${unit.descriptorName}-${index}`}
                      className="h-3 w-3"
                    />
                    <Label
                      htmlFor={`weapon-${unit.descriptorName}-${index}`}
                      className="cursor-pointer text-xs font-medium whitespace-nowrap"
                    >
                      {weapon.ammo.name} {weapon.numberOfWeapons > 1 && `x${weapon.numberOfWeapons}`}
                    </Label>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

